import { action, computed, flow, observable } from "mobx";
import { useContext, useEffect } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { CvpDeleteAnswerResponse } from "../../../../api/questionForm/CvpAnswerQuestion";
import {
  AnswersDto,
  CvpGetAnswersResponse,
} from "../../../../api/questionForm/CvpGetAnswers";
import { CvpGetQuestionFormLogsResponse } from "../../../../api/questionForm/CvpGetQuestionFormLogs";
import {
  Answers,
  FormQuestion,
  Option,
  QuestionFormLog,
} from "../../../../api/questionForm/QuestionFormTypes";
import { Store2 } from "../../../../WSUtils/Store";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { CVEditContext, CVEditContextStore } from "../../CVEditContext";
import { CVEQuestionStepContext } from "../CVEQuestionStepContext";
import { CVEQuestionStepContextStore } from "../CVEQuestionStepContextStore";
import { getQuestionFormToken } from "../getQuestionFormToken";
import { validateQuestion } from "../QuestionType";
import { useQuestionFormAnswers } from "../useQuestionFormAnswers";
import { useQuestionFormLog } from "../useQuestionFormLog";

export class CVEChoiceStore extends Store2<any> {
  @computed get isChecked() {
    if (this.answer) {
      return !!this.answerId;
    }
    return false;
  }

  @observable answer: Answers = null;
  @observable answerId: number = null;

  @observable forms: CvpGetQuestionFormLogsResponse;
  @observable answers: CvpGetAnswersResponse;

  question: FormQuestion;
  userInfo: UserInfo = null;
  cvEditContext: CVEditContextStore = null;
  questionContext: CVEQuestionStepContextStore = null;
  queryClient: QueryClient;

  _onRender = (question: FormQuestion, option: Option) => {
    this.question = question;
    this.userInfo = useContext(UserInfoContext);
    this.cvEditContext = useContext(CVEditContext);
    this.questionContext = useContext(CVEQuestionStepContext);

    this.queryClient = useQueryClient();

    const { qfl } = useQuestionFormLog(question.questionFormId);
    const { answers } = useQuestionFormAnswers(qfl?.id);

    // put stuff in mobx state
    useEffect(
      action(() => {
        this.qfl = qfl;
        this.answers = answers;

        if (!answers) return;

        let a = answers.items.find((x) => x.questionOptionId == option.id);

        if (a) {
          this.answer = a;
          this.answerId = a.id;
        } else {
          const { irel, trel } = this.cvEditContext?.applyingForJob ?? {};
          const newAnswer = {
            relatedId: irel,
            relatedType: trel,
            questionId: question.id,
            questionOptionId: !!option ? option.id : question.options[0].id,
            numberAnswer: 0,
            stringAnswer: "",
            questionFormLog: qfl.id,
          };

          this.answer = newAnswer;
          this.answerId = null;
        }
      }),
      [qfl, answers] // only apply new mobx state when forms or answers have changed
    );
  };

  @observable qfl: QuestionFormLog;

  onChange = flow(function* (checked: boolean) {
    const t: CVEChoiceStore = this;
    const questionFormAPI = t.userInfo.fullAPI.questionFormAPI;
    const token = getQuestionFormToken();

    if (checked) {
      let answersToDelete: AnswersDto[] = [];

      if (!t.question.allowMultiple) {
        answersToDelete = t.answers.items.filter(
          (x) => x.questionId == this.question.id
        );
      }

      yield Promise.all(
        answersToDelete.map((a) => {
          if (token) {
            return questionFormAPI.deleteAnswerAnonymous({
              showSentryDialogOnError: true,
              userInfo: t.userInfo,
              token,
              id: a.id,
            });
          }

          return questionFormAPI.deleteAnswer({
            showSentryDialogOnError: true,
            userInfo: t.userInfo,
            id: a.id,
          });
        })
      );

      if (token) {
        yield questionFormAPI.answerQuestionAnonymous({
          showSentryDialogOnError: true,
          userInfo: t.userInfo,
          token,
          answer: {
            clearAnswersFirst: false,
            id: t.answer.id,
            numberAnswer: t.answer.numberAnswer,
            questionFormLog: t.answer.questionFormLog,
            questionId: t.answer.questionId,
            questionOptionId: t.answer.questionOptionId,
            stringAnswer: t.answer.stringAnswer,
            relatedId: t.qfl.receiverRelatedId,
            relatedType: t.qfl.receiverRelatedType,
          },
        });
      } else {
        yield questionFormAPI.answerQuestion({
          showSentryDialogOnError: true,
          userInfo: t.userInfo,

          //
          clearAnswersFirst: false,
          id: t.answer.id,
          numberAnswer: t.answer.numberAnswer,
          questionFormLog: t.answer.questionFormLog,
          questionId: t.answer.questionId,
          questionOptionId: t.answer.questionOptionId,
          stringAnswer: t.answer.stringAnswer,
          relatedId: t.qfl.receiverRelatedId,
          relatedType: t.qfl.receiverRelatedType,
        });
      }

      invalidateQueries(t.queryClient, [
        "questionFormAPI.getAnswers",
        t.qfl.id,
      ]);
    } else {
      let res: CvpDeleteAnswerResponse;

      if (token) {
        res = yield questionFormAPI.deleteAnswerAnonymous({
          showSentryDialogOnError: true,
          userInfo: t.userInfo,
          token,
          id: t.answer.id,
        });
      } else {
        res = yield questionFormAPI.deleteAnswer({
          showSentryDialogOnError: true,
          userInfo: t.userInfo,
          id: t.answerId,
        });
      }

      if (res.ok) {
        invalidateQueries(t.queryClient, [
          "questionFormAPI.getAnswers",
          t.qfl.id,
        ]);

        t.answer.id = null;
        t.answerId = null;
      }
    }
  }).bind(this);
}
